/**
 * Owner-authored profile demographics editor.
 *
 * Triggered from a settings icon on each row in `ListProfiles`. The
 * backend's `setProfilePreferences` resolver enforces that only the
 * linked owner (or admin) can mutate a profile's preferences, so the
 * dialog is safe to render even when the caller doesn't own the
 * profile — the save will fail with a clear error.
 *
 * Scope is intentionally minimal for now: just `age` and `gender`.
 * Content-policy modes (religion / politics / language) live on the
 * same `ProfilePreferences` row but are out of scope for this
 * iteration; we can add them here in the same shape later.
 */
import React, {useEffect, useMemo, useState} from 'react';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import {ProfileGender} from '../../types/generated/graphql';
import {
  useGetProfilePreferences,
  useSetProfilePreferences,
} from '../../api/graphql_profile_preferences';

interface Props {
  client: ApolloClient<NormalizedCacheObject>;
  profileId: string | null;
  profileName?: string;
  open: boolean;
  onClose: () => void;
}

/**
 * Compute current age in whole years from a YYYY-MM-DD string.
 * Mirrors the server-side helper in profile_preferences.ts so the
 * dialog can show the same number the API will return after save.
 */
function computeAgeFromDate(birthdate: string): number | null {
  if (!birthdate) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(birthdate);
  if (!m) return null;
  const [, ys, ms, ds] = m;
  const birth = new Date(Number(ys), Number(ms) - 1, Number(ds));
  if (Number.isNaN(birth.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const beforeBirthdayThisYear =
    now.getMonth() < birth.getMonth() ||
    (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate());
  if (beforeBirthdayThisYear) age -= 1;
  return age >= 0 ? age : null;
}

export function ProfileSettingsDialog({
  client,
  profileId,
  profileName,
  open,
  onClose,
}: Props) {
  const {data, loading} = useGetProfilePreferences(client, open ? profileId : null);
  const [setPrefs, {loading: saving}] = useSetProfilePreferences(client);

  // Local edit state — seeded from the fetched preferences and only
  // committed to the server on Save. Storing birthdate (instead of
  // age) means we don't have to "rotate" stored ages on a birthday;
  // the API derives the current age every time it returns the
  // preferences.
  const [birthdate, setBirthdate] = useState<string>('');
  const [gender, setGender] = useState<ProfileGender | ''>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (data?.getProfilePreferences) {
      const p = data.getProfilePreferences;
      setBirthdate(p.birthdate ?? '');
      setGender((p.gender as ProfileGender | null) ?? '');
    } else if (open && !loading) {
      setBirthdate('');
      setGender('');
    }
  }, [data, open, loading]);

  // Display-only age derived from the chosen birthdate. Used in the
  // helper text under the field so the owner can sanity-check the
  // year they typed without waiting for a server round-trip.
  const previewAge = useMemo(() => computeAgeFromDate(birthdate), [birthdate]);

  const handleSave = async () => {
    if (!profileId) return;
    setError(null);

    // Validate birthdate: empty clears, otherwise must be a real
    // date in the past and not absurdly old (catches typos like
    // 19xx where they meant 20xx).
    let payloadBirthdate: string | null = null;
    if (birthdate.trim() !== '') {
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(birthdate);
      if (!m) {
        setError('Birthdate must be in YYYY-MM-DD format.');
        return;
      }
      const d = new Date(birthdate);
      if (Number.isNaN(d.getTime())) {
        setError('Birthdate is not a valid date.');
        return;
      }
      if (d.getTime() > Date.now()) {
        setError('Birthdate cannot be in the future.');
        return;
      }
      const ageNow = computeAgeFromDate(birthdate);
      if (ageNow != null && ageNow > 120) {
        setError('That birthdate would imply an age over 120 — please re-check.');
        return;
      }
      payloadBirthdate = birthdate;
    }

    try {
      await setPrefs({
        variables: {
          profileId,
          input: {
            birthdate: payloadBirthdate,
            gender: gender === '' ? null : gender,
          },
        },
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        Profile settings
        {profileName && (
          <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
            {profileName}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, mt: 1}}>
            <TextField
              label="Birthdate"
              type="date"
              value={birthdate}
              onChange={e => setBirthdate(e.target.value)}
              InputLabelProps={{shrink: true}}
              helperText={
                previewAge != null
                  ? `Age today: ${previewAge}. The age-band verdict on the video page uses this.`
                  : 'Birthdate of the person who uses this profile. Used to pick the right age-band verdict.'
              }
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="gender-label">Gender</InputLabel>
              <Select
                labelId="gender-label"
                label="Gender"
                value={gender}
                onChange={e => setGender(e.target.value as ProfileGender | '')}
              >
                {/* Empty value = "not specified" (clears the field). */}
                <MenuItem value="">
                  <em>Not specified</em>
                </MenuItem>
                <MenuItem value={ProfileGender.Male}>Male</MenuItem>
                <MenuItem value={ProfileGender.Female}>Female</MenuItem>
                <MenuItem value={ProfileGender.NonBinary}>Non-binary</MenuItem>
                <MenuItem value={ProfileGender.PreferNotToSay}>
                  Prefer not to say
                </MenuItem>
              </Select>
            </FormControl>
            {error && (
              <Alert severity="error" variant="outlined" sx={{fontSize: 13}}>
                {error}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading || !profileId}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
